import { HeaderButton } from 'components/common';
import { ChatIcon, FilterIcon } from 'components/common/Icons';
import { FilterInput, ViewButton, ViewToggle } from 'components/core';
import { useContext } from 'react';
import { ColumnContext, FilterContext, ViewContext } from 'utils/context';
import DK from 'utils/dictionaries/filter_dictionary';
import { usePermissions } from 'utils/hooks';

import { Flex, Icon } from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';

const ONE_DIGIT_LIMIT = 10;

const getStyles = (opts: { count: number }) => {
  return {
    headerBtn: {
      '&::after': {
        content: `"${opts.count}"`,
        width: 'min-content',
        height: '18px',
        position: 'absolute',
        backgroundColor: 'white',
        top: '-9.5px',
        borderRadius: '100px',
        left: opts.count < ONE_DIGIT_LIMIT ? '88px' : '84px',
        color: '#3142C8',
        fontSize: 'x-small',
        paddingY: 0,
        paddingX: '6px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'semibold',
      },
      '&:focus::after': {
        outline: '1px solid transparent',
        boxShadow: '#a3ace6 0px 0px 0px 2px',
        borderColor: 'white',
      },
    },
    icon: {
      fontSize: '20px',
      width: '18px',
    },
  };
};

const columnStyle = {
  headerBtn: {
    '&::after': {
      alignItems: 'center',
      backgroundColor: '#3142C8',
      border: '1px solid white',
      borderColor: '#FFFFFF',
      borderRadius: '100px',
      color: '#3142C8',
      display: 'flex',
      fontSize: 'x-small',
      fontWeight: 'semibold',
      height: '12px',
      justifyContent: 'center',
      left: '110px',
      paddingY: 0,
      paddingX: 0,
      position: 'absolute',
      top: '-6px',
      width: '12px',
    },
    '&:focus::after': {
      borderColor: 'white',
      boxShadow: '#a3ace6 0px 0px 0px 2px',
      outline: '1px solid transparent',
    },
  },
  icon: {
    fontSize: '20px',
    width: '18px',
  },
};

interface HeaderProps {
  doSearch: () => void;
  isChatOpen?: boolean;
  isColumnOpen?: boolean;
  isFilterOpen?: boolean;
  onClickChat?: () => void;
  onClickColumn?: () => void;
  onClickFilter?: () => void;
  totalOrders?: number;
  totalShipments?: number;
}

function Header({
  doSearch,
  isChatOpen,
  isColumnOpen = false,
  isFilterOpen = false,
  onClickChat,
  onClickColumn,
  onClickFilter,
  totalOrders,
  totalShipments,
}: HeaderProps) {
  const { t } = useTranslation();
  const { getFilterCount } = useContext(FilterContext);
  const { viewType, selectedView } = useContext(ViewContext);
  const { hasColumnsModified } = useContext(ColumnContext);
  const {
    permissions: { canViewChat },
  } = usePermissions();

  const filterCount = getFilterCount();
  const styles = getStyles({ count: filterCount });
  const placeholder = t('core.searchEntity', {
    entity: t(viewType === 'order' ? 'core.orders' : 'core.shipments'),
  });
  const isNotSavedView = ['all-shipments', 'all-orders'].includes(
    selectedView?.id || '',
  );
  return (
    <Flex
      data-testid="page-layout-header"
      align="center"
      justify="between"
      css={{ margin: '0 0 $medium' }}
    >
      <ViewToggle totalOrders={totalOrders} totalShipments={totalShipments} />
      <Flex gap="medium">
        <FilterInput
          id={DK.SEARCH_TEXT}
          doSearch={doSearch}
          placeholder={placeholder}
        />
        {canViewChat && (
          <HeaderButton
            data-testid="header-chat-button"
            onClick={onClickChat}
            hasModalActive={isChatOpen}
            startIcon={<ChatIcon size="18px" fill="white" />}
            children={t('core.chat.movementGpt')}
          />
        )}
        <HeaderButton
          data-testid="header-filter-button"
          onClick={onClickFilter}
          startIcon={<FilterIcon size="18px" fill="white" />}
          sx={{ ...(filterCount && styles.headerBtn) }}
          hasModalActive={isFilterOpen}
          children={t('global.filter')}
        />
        <HeaderButton
          data-testid="header-column-button"
          onClick={onClickColumn}
          startIcon={<Icon icon="view_week" style={styles.icon} />}
          sx={{
            ...(hasColumnsModified && isNotSavedView && columnStyle.headerBtn),
          }}
          hasModalActive={isColumnOpen}
          children={t('core.column')}
        />
        <ViewButton doSearch={doSearch} />
      </Flex>
    </Flex>
  );
}

export default Header;
